import { Flex, Modal, Typography } from '@alpine-iq/ui-library'
import { useEffect, useMemo, useRef, useState } from 'react'

import WidgetButton from '@/components/primatives/WidgetButton'
import { useDisplayTrigger } from '@/hooks/useDisplayTrigger'
import { isFrequencyActive } from '@/utils/displayFrequency'
import { useFlowState } from '@/stores/useFlowState'
import { useSessionStore } from '@/stores/useSessionStore'
import { DemoAction, useWidgetStore } from '@/stores/useWidgetStore'
import type { DisplaySettings, DynamicDisplayFrequencyUnit, WidgetPopupSettings } from '@/types'
import { WidgetStep } from '@/types'
import { showInfoToast } from '@/utils'
import CoverImage from './CoverImage'
import LogoImage from './LogoImage'
import { useBaseOverrides } from '@/hooks/useBaseOverrides'
import { track } from '@/utils/analytics'

const POPUP_DISMISSED_PREFIX = 'widget-popup-dismissed:'

function getPopupDismissedKey(uid: string): string {
  return `${POPUP_DISMISSED_PREFIX}${uid}`
}

function isPopupDismissedForUID(uid: string | undefined): boolean {
  if (!uid) return false
  return localStorage.getItem(getPopupDismissedKey(uid)) === 'true'
}

function markPopupDismissedForUID(uid: string | undefined): void {
  if (!uid) return
  localStorage.setItem(getPopupDismissedKey(uid), 'true')
}

const DEFAULT_POPUP_DISPLAY_SETTINGS: DisplaySettings = {
  triggers: {
    onLoad: { enabled: true, actionDelay: 60 },
  } as DisplaySettings['triggers'],
  displayFrequency: { enabled: true, count: 1, unit: 'minute' },
}

const UNIT_TO_MS: Record<DynamicDisplayFrequencyUnit, number> = {
  second: 1_000,
  minute: 60_000,
  hour: 3_600_000,
  day: 86_400_000,
  week: 604_800_000,
  month: 2_592_000_000,
  year: 31_536_000_000,
}

function getFrequencyDelayMs(displaySettings: DisplaySettings): number {
  const freq = displaySettings.displayFrequency
  if (!isFrequencyActive(freq)) return 0
  return (freq!.count ?? 1) * (UNIT_TO_MS[freq!.unit ?? 'minute'] ?? 60_000)
}

export function isDemoShowPopupEnabled(demo: boolean, demoAction: DemoAction | null): boolean {
  return demo && demoAction?.type === 'SHOW_POPUP'
}

export default function PopupModal() {
  const baseOverrides = useBaseOverrides()
  const {
    isFloatingWidgetMode,
    floatingWidgetOpen,
    setFloatingWidgetOpen,
    // Demo mode specific
    demo,
    demoAction,
  } = useWidgetStore()
  const { setCurrentStep, currentStep } = useFlowState()
  const widgetSettings = useSessionStore((state) => state.widgetSettings)
  const popupSettings = widgetSettings?.popupSettings
  const currentUID = useSessionStore((state) => state.currentUID)

  const displaySettings = useMemo(
    () => popupSettings?.displaySettings ?? DEFAULT_POPUP_DISPLAY_SETTINGS,
    [popupSettings?.displaySettings]
  )
  const hasDisplayFrequency = isFrequencyActive(displaySettings.displayFrequency)

  const [isDismissed, setIsDismissed] = useState(() => {
    if (hasDisplayFrequency) return false
    return isPopupDismissedForUID(currentUID)
  })
  const prevFloatingOpen = useRef(floatingWidgetOpen)

  const displayReady = useDisplayTrigger(displaySettings, 'popup-modal', !!widgetSettings)

  const isDemoShowPopup = isDemoShowPopupEnabled(demo, demoAction)

  useEffect(() => {
    if (isDemoShowPopup) {
      setIsDismissed(false)
    }
  }, [isDemoShowPopup])

  useEffect(() => {
    prevFloatingOpen.current = floatingWidgetOpen
  }, [floatingWidgetOpen])

  // Re-show popup after dismiss when display frequency is configured
  useEffect(() => {
    if (!isDismissed || !hasDisplayFrequency) return
    const delayMs = getFrequencyDelayMs(displaySettings)
    if (delayMs <= 0) return
    const timer = setTimeout(() => setIsDismissed(false), delayMs)
    return () => clearTimeout(timer)
  }, [isDismissed, hasDisplayFrequency, displaySettings])

  const enabled = popupSettings?.enabled === true
  const shouldShow = isDemoShowPopup
    ? true
    : enabled && isFloatingWidgetMode && !floatingWidgetOpen && !isDismissed && displayReady && !demo

  const hasTrackedShown = useRef(false)

  useEffect(() => {
    if (shouldShow && !hasTrackedShown.current) {
      hasTrackedShown.current = true
      track('widget', 'widget_popup_shown', {
        headerText: popupSettings?.headerText ?? '',
        headerSubext: popupSettings?.headerSubext ?? '',
        bodyText: popupSettings?.bodyText ?? '',
      })
    }
    if (!shouldShow) {
      hasTrackedShown.current = false
    }
  }, [shouldShow, popupSettings?.headerText, popupSettings?.headerSubext, popupSettings?.bodyText])

  if (!shouldShow) return null

  const popupTrackDetails = {
    headerText: popupSettings?.headerText ?? '',
    headerSubext: popupSettings?.headerSubext ?? '',
    bodyText: popupSettings?.bodyText ?? '',
  }

  const handleTakeQuiz = () => {
    track('widget', 'widget_popup_clicked', { ...popupTrackDetails, action: 'open' })
    if (isDemoShowPopup) {
      showInfoToast('Demo mode: Popup modal is enabled')
      return
    }
    setFloatingWidgetOpen(true)
    if (!currentStep) {
      setCurrentStep(WidgetStep.Welcome)
    }
  }

  const handleClose = () => {
    track('widget', 'widget_popup_clicked', { ...popupTrackDetails, action: 'close' })
    if (isDemoShowPopup) return
    if (!hasDisplayFrequency) {
      markPopupDismissedForUID(currentUID)
    }
    setIsDismissed(true)
  }

  const { pageStyle } = popupSettings as WidgetPopupSettings

  const backgroundType = pageStyle?.backgroundType || 'color'
  const backgroundColor = pageStyle?.bg || '#ffffff'
  const backgroundImage = pageStyle?.backgroundImage || ''

  return (
    <Modal open={true} onClose={handleClose} closeBtn>
      <div
        style={{
          height: '100%',
          overflow: 'hidden',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          // Background
          backgroundImage: backgroundType === 'image' ? `url(${backgroundImage})` : undefined,
          backgroundColor: backgroundType === 'color' ? backgroundColor : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',

          position: 'relative',
        }}
      >
        <Flex
          column
          gap='24'
          css={`
							padding-bottom: 24px;
						`}
        >
          {/* Cover image */}
          <CoverImage />

          <Flex center column>
            <Flex column gap='16' padding='0px 24px' style={{ maxWidth: 500, width: '100%' }}>
              <LogoImage />
              <Typography
                variant='h2'
                style={{ textAlign: 'center', color: baseOverrides.textPrimaryColor }}
                baseOverrides={baseOverrides}
              >
                {popupSettings?.headerText || 'Take our cannabis quiz!'}
              </Typography>
              {popupSettings?.headerSubext && (
                <Typography
                  variant='body-sm'
                  style={{ textAlign: 'center', color: baseOverrides.textSecondaryColor }}
                  baseOverrides={baseOverrides}
                >
                  {popupSettings?.headerSubext || ''}
                </Typography>
              )}
              <Typography
                variant='body'
                style={{ textAlign: 'center', color: baseOverrides.textPrimaryColor }}
                baseOverrides={baseOverrides}
              >
                {popupSettings?.bodyText || 'Just 5 easy questions to get your personalized recommendation!'}
              </Typography>
              <WidgetButton
                variant='primary'
                onClick={handleTakeQuiz}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  borderRadius: 999,
                }}
              >
                {popupSettings?.style?.buttonPrimary?.textDefault || 'Take the quiz'}
              </WidgetButton>
            </Flex>
          </Flex>
        </Flex>
      </div>
    </Modal>
  )
}
